export function WhistlerEnforcement() {
  const violations = [
    {
      violation: 'Operating STR in non-tourist zone',
      penalty: '$2,000 - $10,000',
      enforcement: 'Immediate cease and desist',
      severity: 'critical'
    },
    {
      violation: 'Operating without business license',
      penalty: '$1,000 - $5,000',
      enforcement: 'Daily fines until compliance',
      severity: 'high'
    },
    {
      violation: 'Zoning bylaw violations',
      penalty: '$2,000+',
      enforcement: 'Stop-work order + daily fines',
      severity: 'critical'
    },
    {
      violation: 'Fire safety non-compliance',
      penalty: '$1,500 - $5,000',
      enforcement: 'License suspension',
      severity: 'high'
    },
    {
      violation: 'Exceeding occupancy limits',
      penalty: '$1,000 - $3,000',
      enforcement: 'Warning then fines',
      severity: 'medium'
    },
    {
      violation: 'Noise or nuisance violations',
      penalty: '$500 - $2,000',
      enforcement: 'Escalating fines',
      severity: 'medium'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-[#882629]/10 text-[#882629] px-4 py-2 rounded-full text-sm font-medium mb-4">
            2026 Enforcement
          </span>
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Zoning Bylaw Officer Enforcement
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Whistler takes zoning violations extremely seriously. Operating an STR in a non-tourist zone can result in massive fines of $2,000+ and immediate shutdown orders from dedicated bylaw officers.
          </p>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-8 text-center">
            Common Violations &amp; Penalties
          </h3>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#882629] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Violation</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Penalty Range</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Enforcement Action</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Severity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {violations.map((item, index) => (
                    <tr key={index} className="hover:bg-[#F0F0F0]/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-[#505A5A]">{item.violation}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#882629]">{item.penalty}</td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/70">{item.enforcement}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          item.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          item.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.severity.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-[#882629]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-map-pin-line text-3xl text-[#882629]"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#505A5A] mb-4">Zoning Verification</h3>
            <p className="text-[#505A5A]/70 leading-relaxed">
              Bylaw officers actively verify property zoning designations and cross-reference with STR listings to identify non-compliant properties.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-[#882629]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-search-eye-line text-3xl text-[#882629]"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#505A5A] mb-4">Platform Monitoring</h3>
            <p className="text-[#505A5A]/70 leading-relaxed">
              Regular audits of Airbnb, Vrbo, and other platforms to identify unlicensed or improperly zoned STR operations.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-[#882629]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-user-voice-line text-3xl text-[#882629]"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#505A5A] mb-4">Complaint Response</h3>
            <p className="text-[#505A5A]/70 leading-relaxed">
              Neighbor complaints are investigated promptly, with site visits and enforcement action taken within 48-72 hours.
            </p>
          </div>
        </div>

        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 mb-12">
          <div className="flex items-start gap-4">
            <i className="ri-alert-line text-3xl text-red-600 flex-shrink-0"></i>
            <div>
              <h4 className="text-xl font-bold text-red-900 mb-3">
                Critical: Verify Your Zoning Before Listing
              </h4>
              <p className="text-red-800 leading-relaxed mb-4">
                The most common and expensive violation in Whistler is operating an STR in a residential zone (RS, RM) instead of a Tourist Accommodation zone (TA1, TA2, TA3). This violation carries fines starting at $2,000 and can result in immediate shutdown.
              </p>
              <p className="text-red-800 leading-relaxed">
                <strong>Before purchasing or listing any property:</strong> Verify the exact zoning designation with the Resort Municipality of Whistler. Properties in Alpine Meadows, Emerald Estates, and White Gold are typically residential zones where STRs are prohibited.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#882629] to-[#6E1F22] rounded-2xl p-8 lg:p-10 text-white shadow-xl">
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <i className="ri-shield-cross-line text-6xl"></i>
            </div>
            <div className="flex-grow text-center lg:text-left">
              <h4 className="text-2xl font-bold mb-3">
                Enforcement Style: Zoning Bylaw Officers
              </h4>
              <p className="text-lg text-white/90 leading-relaxed">
                Whistler employs dedicated bylaw officers who actively monitor STR compliance. Massive fines of $2,000+ are issued for operating in non-tourist zones. Zoning violations result in immediate cease and desist orders with daily fines until compliance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
